import { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api-service';
import Layout from './Layout';
import MetaTags from './meta-tags';
import scrollRestoration from '../utils/scroll-restoration';
import { useScrollRestoration } from '../hooks/use-scroll-restoration';

const CINEMA_LOCATIONS = [
  { key: 'vilnius', name: 'Forum Cinemas Vingis', city: 'Vilnius', chain: 'forum' },
  { key: 'kaunas', name: 'Forum Cinemas Kaunas', city: 'Kaunas', chain: 'forum' },
  { key: 'klaipeda', name: 'Forum Cinemas Klaipėda', city: 'Klaipėda', chain: 'forum' },
  { key: 'siauliai', name: 'Forum Cinemas Šiauliai', city: 'Šiauliai', chain: 'forum' },
  { key: 'cinamon-kaunas', name: 'Cinamon Kaunas (Mega)', city: 'Kaunas', chain: 'cinamon' }
];

function buildTicketUrl(show, movie) {
  // Cinamon ticket URL - use pid for seat-plan
  if (movie.cinemaSlug && show.pid) {
    return `https://cinamonkino.com/${movie.cinemaSlug}/seat-plan/${show.pid}/lt`;
  }
  // Forum Cinemas ticket URL
  if (show.id && movie.cinemaId) {
    return `https://forumcinemas.lt/lt/ekranas/?id=${show.id}&cid=${movie.cinemaId}`;
  }
  return null;
}

function getTodayDate() {
  return new Date().toISOString().split('T')[0];
}

function formatTime(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleTimeString('lt-LT', { hour: '2-digit', minute: '2-digit' });
}

function formatDisplayDate(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('lt-LT', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
}

// Age rating badge colors
function getAgeRatingColor(symbol) {
  switch (symbol) {
    case '0': return 'bg-green-600/20 text-green-400 border-green-500/30';
    case '1': return 'bg-blue-600/20 text-blue-400 border-blue-500/30';
    case '2': return 'bg-yellow-600/20 text-yellow-400 border-yellow-500/30';
    case '3': return 'bg-red-600/20 text-red-400 border-red-500/30';
    default: return 'bg-gray-600/20 text-gray-400 border-gray-500/30';
  }
}

function getAgeRatingLabel(symbol) {
  switch (symbol) {
    case '0': return 'V';
    case '1': return 'N-7';
    case '2': return 'N-13';
    case '3': return 'N-16';
    default: return '';
  }
}

// Individual movie card with showtimes
const MovieShowtimeCard = memo(({ movie, onNavigate, locationKey }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasDescription = movie.description && movie.description.length > 0;
  const truncatedDesc = hasDescription 
    ? (movie.description.length > 200 ? movie.description.substring(0, 200) + '...' : movie.description)
    : '';
  const hasLink = !!movie.tmdbId;
  const handleClick = () => {
    if (hasLink) {
      if (locationKey) {
        scrollRestoration.saveScrollPosition(locationKey, window.scrollY);
        scrollRestoration.setNavigatingToDetail(locationKey);
      }
      onNavigate(`/movie/${movie.tmdbId}`);
    }
  };

  return (
    <div className="bg-gradient-to-br from-dark-800 to-dark-900 rounded-xl sm:rounded-2xl overflow-hidden border border-dark-700/50 hover:border-dark-600/70 transition-all shadow-lg">
      <div className="flex flex-col sm:flex-row">
        {/* Poster */}
        <div 
          className={`flex-shrink-0 w-full sm:w-40 md:w-48 ${hasLink ? 'cursor-pointer group/poster' : ''}`}
          onClick={handleClick}
          role={hasLink ? 'link' : undefined}
          title={hasLink ? `Žiūrėti ${movie.title} informaciją` : undefined}
        >
          {movie.poster ? (
            <div className="relative w-full aspect-[2/3] sm:h-full sm:aspect-auto overflow-hidden">
              <img
                src={movie.poster}
                alt={movie.title}
                className={`absolute inset-0 w-full h-full object-cover transition-transform duration-300 ${hasLink ? 'group-hover/poster:scale-105' : ''}`}
                loading="lazy"
              />
              {hasLink && (
                <div className="absolute inset-0 bg-black/0 group-hover/poster:bg-black/30 transition-colors flex items-center justify-center">
                  <div className="opacity-0 group-hover/poster:opacity-100 transition-opacity bg-white/20 backdrop-blur-sm rounded-full p-3">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="w-full aspect-[2/3] sm:h-full sm:aspect-auto bg-dark-700 flex items-center justify-center">
              <svg className="w-12 h-12 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
              </svg>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 p-3 sm:p-5 min-w-0">
          {/* Title row */}
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="min-w-0 flex-1">
              <h3 
                className={`text-base sm:text-lg md:text-xl font-bold leading-tight ${hasLink ? 'text-white hover:text-primary-300 cursor-pointer transition-colors' : 'text-white'}`}
                onClick={handleClick}
              >
                {movie.title}
              </h3>
              {movie.originalTitle && movie.originalTitle !== movie.title && (
                <p className="text-xs sm:text-sm text-gray-400 mt-0.5 line-clamp-1">
                  {movie.originalTitle}
                </p>
              )}
            </div>
            {movie.ageSymbol && (
              <span className={`flex-shrink-0 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg text-xs font-bold border ${getAgeRatingColor(movie.ageSymbol)}`}>
                {getAgeRatingLabel(movie.ageSymbol)}
              </span>
            )}
          </div>

          {/* Meta info */}
          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-2 text-xs sm:text-sm text-gray-400">
            {movie.duration > 0 && (
              <span className="flex items-center gap-1">
                <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {movie.duration} min
              </span>
            )}
            {movie.genres.length > 0 && (
              <span className="flex items-center gap-1 line-clamp-1">
                <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                <span className="truncate">{movie.genres.join(', ')}</span>
              </span>
            )}
            {movie.director && (
              <span className="flex items-center gap-1 w-full sm:w-auto">
                <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span className="truncate">{movie.director}</span>
              </span>
            )}
          </div>

          {/* Ratings */}
          {(movie.ratings?.imdb || movie.ratings?.rt || movie.imdbRating) && (
            <div className="flex flex-wrap items-center gap-1.5 mb-2 sm:mb-3">
              {(movie.ratings?.imdb || movie.imdbRating) && (
                <span className="flex items-center gap-1 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg text-xs font-bold border bg-yellow-600/20 text-yellow-400 border-yellow-500/30">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  {movie.ratings?.imdb != null ? movie.ratings.imdb : movie.imdbRating} IMDb
                </span>
              )}
              {movie.ratings?.rt != null && (
                <span className={`flex items-center gap-1 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-lg text-xs font-bold border ${
                  movie.ratings.rt >= 60 
                    ? 'bg-red-600/20 text-red-400 border-red-500/30' 
                    : 'bg-green-600/20 text-green-400 border-green-500/30'
                }`}>
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                  </svg>
                  {movie.ratings.rt}% RT
                </span>
              )}
            </div>
          )}

          {/* Description */}
          {hasDescription && (
            <div className="mb-2 sm:mb-3">
              <p className="text-xs sm:text-sm text-gray-300 leading-relaxed">
                {isExpanded ? movie.description : truncatedDesc}
              </p>
              {movie.description.length > 200 && (
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="text-primary-400 hover:text-primary-300 text-xs sm:text-sm font-medium mt-1 transition-colors"
                >
                  {isExpanded ? 'Rodyti mažiau' : 'Skaityti daugiau'}
                </button>
              )}
            </div>
          )}

          {/* Showtimes */}
          {movie.showtimes && movie.showtimes.length > 0 ? (
            <div className="mt-2 sm:mt-3">
              <h4 className="text-xs sm:text-sm font-semibold text-gray-300 mb-2 flex items-center gap-1.5">
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Seansai ({movie.showtimes.length})
              </h4>
              <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-1.5 sm:gap-2">
                {movie.showtimes.map((show, idx) => {
                  const ticketUrl = buildTicketUrl(show, movie);
                  return (
                    <a
                      key={show.id || idx}
                      href={ticketUrl || '#'}
                      target={ticketUrl ? '_blank' : undefined}
                      rel={ticketUrl ? 'noopener noreferrer' : undefined}
                      className={`inline-flex flex-col items-center justify-center px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all border min-h-[60px] sm:min-h-[70px] ${
                        ticketUrl
                          ? 'bg-dark-700/60 text-gray-300 border-dark-600/50 hover:border-primary-500/50 hover:bg-primary-600/20 active:scale-95 sm:hover:scale-105 cursor-pointer'
                          : 'bg-dark-700/60 text-gray-300 border-dark-600/50'
                      }`}
                      title={ticketUrl ? 'Pirkti bilietą' : undefined}
                    >
                      <span className="text-sm sm:text-base font-bold text-white whitespace-nowrap">{formatTime(show.startTime)}</span>
                      {show.release && (
                        <span className="text-[10px] sm:text-xs text-gray-400 mt-0.5 sm:mt-1 max-w-[100px] sm:max-w-[140px] text-center leading-tight line-clamp-2" title={show.release}>
                          {show.release}
                        </span>
                      )}
                      <div className="flex items-center gap-1 mt-0.5">
                        {show.is3D && (
                          <span className="text-[10px] sm:text-xs text-primary-400 font-bold">3D</span>
                        )}
                        {show.soundType && show.soundType !== '5.1' && (
                          <span className="text-[10px] sm:text-xs text-gray-500">{show.soundType}</span>
                        )}
                      </div>
                    </a>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="mt-2 sm:mt-3 flex items-center gap-2 text-xs sm:text-sm text-gray-500">
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Šiai dienai seansų nėra</span>
            </div>
          )}

          {/* Trailer link */}
          {movie.trailer && (
            <a
              href={movie.trailer}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 mt-2 sm:mt-3 text-xs sm:text-sm text-primary-400 hover:text-primary-300 transition-colors font-medium"
            >
              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
              </svg>
              Žiūrėti anonsą
            </a>
          )}
        </div>
      </div>
    </div>
  );
});

MovieShowtimeCard.displayName = 'MovieShowtimeCard';

// Loading skeleton
const LoadingSkeleton = () => (
  <div className="space-y-3 sm:space-y-4">
    {[1, 2, 3, 4].map(i => (
      <div key={i} className="bg-dark-800 rounded-xl sm:rounded-2xl overflow-hidden border border-dark-700/50 animate-pulse">
        <div className="flex flex-col sm:flex-row">
          <div className="w-full sm:w-40 md:w-48 aspect-[2/3] sm:h-64 sm:aspect-auto bg-dark-700" />
          <div className="flex-1 p-3 sm:p-5 space-y-2 sm:space-y-3">
            <div className="h-5 sm:h-6 bg-dark-700 rounded-lg w-3/4" />
            <div className="h-3 sm:h-4 bg-dark-700 rounded-lg w-1/2" />
            <div className="h-3 sm:h-4 bg-dark-700 rounded-lg w-full" />
            <div className="h-3 sm:h-4 bg-dark-700 rounded-lg w-2/3" />
            <div className="grid grid-cols-3 sm:flex gap-1.5 sm:gap-2 mt-3 sm:mt-4">
              <div className="h-14 sm:h-16 bg-dark-700 rounded-lg" />
              <div className="h-14 sm:h-16 bg-dark-700 rounded-lg" />
              <div className="h-14 sm:h-16 bg-dark-700 rounded-lg" />
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

function CinemaShowtimes() {
  const navigate = useNavigate();
  const [selectedCinema, setSelectedCinema] = useState('vilnius');
  const [selectedDate, setSelectedDate] = useState(getTodayDate);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter state
  const [filters, setFilters] = useState({
    imdbRating: 0,
    rtRating: 0,
    selectedGenre: 'all'
  });

  // Temporarily redirect Cinamon selections to Forum Cinemas
  useEffect(() => {
    const cinema = CINEMA_LOCATIONS.find(c => c.key === selectedCinema);
    if (cinema && cinema.chain === 'cinamon') {
      setSelectedCinema('vilnius');
    }
  }, [selectedCinema]);

  const fetchShowtimes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await apiService.getCinemaShowtimes(selectedCinema, selectedDate);
      setData(result);
    } catch (err) {
      console.error('Error fetching cinema showtimes:', err);
      setError(err.message || 'Nepavyko gauti kino seansų');
    } finally {
      setLoading(false);
    }
  }, [selectedCinema, selectedDate]);

  useEffect(() => {
    fetchShowtimes();
  }, [fetchShowtimes]);

  const dateOptions = useMemo(() => {
    const options = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date();
      d.setDate(d.getDate() + i);
      options.push(d.toISOString().split('T')[0]);
    }
    return options;
  }, []);

  const selectedCinemaData = useMemo(
    () => CINEMA_LOCATIONS.find(c => c.key === selectedCinema),
    [selectedCinema]
  );

  // Extract unique genres from movies that have showtimes
  const availableGenres = useMemo(() => {
    if (!data?.movies) return [{ id: 'all', name: 'Visi žanrai' }];
    
    const genreSet = new Set();
    data.movies.forEach(movie => {
      // Only include genres from movies with showtimes
      if (movie.showtimes && movie.showtimes.length > 0) {
        if (movie.genres && Array.isArray(movie.genres)) {
          movie.genres.forEach(genre => genreSet.add(genre));
        }
      }
    });
    
    const genres = [{ id: 'all', name: 'Visi žanrai' }];
    Array.from(genreSet).sort().forEach(genre => {
      genres.push({ id: genre, name: genre });
    });
    
    return genres;
  }, [data?.movies]);

  // Reset genre filter if selected genre is no longer available
  useEffect(() => {
    if (filters.selectedGenre !== 'all') {
      const genreExists = availableGenres.some(g => g.id === filters.selectedGenre);
      if (!genreExists) {
        setFilters(prev => ({ ...prev, selectedGenre: 'all' }));
      }
    }
  }, [availableGenres, filters.selectedGenre]);

  // Scroll restoration for navigating back from movie detail pages
  const locationKey = useScrollRestoration({
    mediaType: `cinema-${selectedCinema}`,
    searchQuery: selectedDate,
    filters,
    isLoading: loading,
    itemsLength: data?.movies?.length || 0
  });

  // Filter movies based on ratings, genre, and showtimes
  const filteredMovies = useMemo(() => {
    if (!data?.movies) return [];
    
    return data.movies.filter(movie => {
      // Hide movies with no showtimes
      if (!movie.showtimes || movie.showtimes.length === 0) {
        return false;
      }
      
      // IMDB rating filter
      if (filters.imdbRating > 0) {
        const imdbRating = movie.ratings?.imdb || movie.imdbRating || 0;
        if (imdbRating < filters.imdbRating) return false;
      }
      
      // Rotten Tomatoes rating filter
      if (filters.rtRating > 0) {
        const rtRating = movie.ratings?.rt || 0;
        if (rtRating < filters.rtRating) return false;
      }
      
      // Genre filter
      if (filters.selectedGenre !== 'all') {
        if (!movie.genres || !movie.genres.includes(filters.selectedGenre)) {
          return false;
        }
      }
      
      return true;
    });
  }, [data?.movies, filters]);

  return (
    <Layout>
      <MetaTags
        title="Kino seansai - Forum Cinemas | FilmųDB"
        description="Peržiūrėkite Forum Cinemas kino seansus Vilniuje, Kaune, Klaipėdoje ir Šiauliuose. Filmų tvarkaraščiai ir bilietų kainos."
        url="https://filmudb.lt/cinema"
        keywords="kino seansai, forum cinemas, kino tvarkaraštis, filmų seansai, kinas vilnius, kinas kaunas"
      />

      <div className="bg-gradient-to-r from-dark-900 via-dark-800 to-dark-900 min-h-screen">
        {/* Hero Section */}
        <section className="relative py-6 sm:py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="text-center mb-6 sm:mb-8">
            <div className="flex items-center justify-center gap-2 sm:gap-3 mb-3 sm:mb-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-red-500 to-red-700 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 sm:w-7 sm:h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
                </svg>
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white">
                Kino <span className="text-gradient">seansai</span>
              </h1>
            </div>
            <p className="text-base sm:text-lg text-gray-300 max-w-2xl mx-auto px-4">
              Forum Cinemas filmų tvarkaraščiai Lietuvoje
            </p>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 sm:gap-4 max-w-3xl mx-auto">
            {/* Cinema selector */}
            <div className="w-full sm:flex-1 sm:max-w-[300px]">
              <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5">Kino teatras</label>
              <div className="relative">
                <select
                  value={selectedCinema}
                  onChange={(e) => setSelectedCinema(e.target.value)}
                  className="w-full h-11 pl-9 sm:pl-10 pr-8 bg-dark-800 border border-dark-600 text-white rounded-xl text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all appearance-none cursor-pointer hover:border-dark-500"
                >
                  <optgroup label="Forum Cinemas">
                    {CINEMA_LOCATIONS.filter(c => c.chain === 'forum').map(cinema => (
                      <option key={cinema.key} value={cinema.key}>
                        {cinema.city} — {cinema.name}
                      </option>
                    ))}
                  </optgroup>
                  {/* Temporarily hidden - Cinamon integration in progress
                  <optgroup label="Cinamon">
                    {CINEMA_LOCATIONS.filter(c => c.chain === 'cinamon').map(cinema => (
                      <option key={cinema.key} value={cinema.key}>
                        {cinema.city} — {cinema.name}
                      </option>
                    ))}
                  </optgroup>
                  */}
                </select>
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Date selector */}
            <div className="w-full sm:w-auto sm:min-w-[200px]">
              <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1.5">Data</label>
              <div className="relative">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  min={getTodayDate()}
                  className="w-full h-11 pl-9 sm:pl-10 pr-4 bg-dark-800 border border-dark-600 text-white rounded-xl text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all cursor-pointer hover:border-dark-500 [color-scheme:dark]"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Quick date buttons */}
          <div className="flex flex-wrap items-center justify-center gap-1.5 sm:gap-2 mt-3 sm:mt-4 max-w-3xl mx-auto px-1">
            {dateOptions.slice(0, 5).map((date, idx) => {
              const d = new Date(date);
              const dayName = idx === 0 ? 'Šiandien' : idx === 1 ? 'Rytoj' : d.toLocaleDateString('lt-LT', { weekday: 'short' });
              const dayNum = d.getDate();
              const isSelected = selectedDate === date;
              
              return (
                <button
                  key={date}
                  onClick={() => setSelectedDate(date)}
                  className={`px-2.5 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium transition-all whitespace-nowrap ${
                    isSelected 
                      ? 'bg-primary-600 text-white shadow-lg shadow-primary-500/25' 
                      : 'bg-dark-700/60 text-gray-300 hover:bg-dark-600/80 hover:text-white border border-dark-600/50'
                  }`}
                >
                  {dayName} {dayNum}
                </button>
              );
            })}
          </div>

          {/* Filters Section */}
          <div className="mt-6 pt-6 border-t border-dark-700/50 max-w-5xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 px-3">
              {/* Genre Filter */}
              <div className="flex flex-col">
                <label className="text-xs text-gray-400 flex items-center space-x-1 h-5 mb-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  <span>Žanras</span>
                </label>
                <select
                  value={filters.selectedGenre}
                  onChange={(e) => setFilters(prev => ({ ...prev, selectedGenre: e.target.value }))}
                  className="h-11 px-3 bg-dark-800 border border-dark-600 text-white rounded-xl text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all cursor-pointer hover:border-dark-500"
                >
                  {availableGenres.map(genre => (
                    <option key={genre.id} value={genre.id}>
                      {genre.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* IMDB Rating Filter */}
              <div className="flex flex-col">
                <label className="text-xs text-gray-400 flex items-center space-x-1 h-5 mb-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  <span>IMDB reitingas</span>
                </label>
                <div className="flex items-center space-x-3 h-11 px-3 bg-dark-800 border border-dark-600 rounded-xl">
                  <input
                    type="range"
                    min={0}
                    max={10}
                    step={0.1}
                    value={filters.imdbRating}
                    onChange={(e) => setFilters(prev => ({ ...prev, imdbRating: parseFloat(e.target.value) }))}
                    className="flex-1 accent-primary-500 cursor-pointer"
                  />
                  <span className="text-sm min-w-[50px] text-right text-white font-medium">
                    {filters.imdbRating > 0 ? `${filters.imdbRating}+` : 'Visi'}
                  </span>
                </div>
              </div>

              {/* RT Rating Filter */}
              <div className="flex flex-col">
                <label className="text-xs text-gray-400 flex items-center space-x-1 h-5 mb-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                  </svg>
                  <span>Rotten Tomatoes</span>
                </label>
                <div className="flex items-center space-x-3 h-11 px-3 bg-dark-800 border border-dark-600 rounded-xl">
                  <input
                    type="range"
                    min={0}
                    max={100}
                    step={1}
                    value={filters.rtRating}
                    onChange={(e) => setFilters(prev => ({ ...prev, rtRating: parseInt(e.target.value) }))}
                    className="flex-1 accent-primary-500 cursor-pointer"
                  />
                  <span className="text-sm min-w-[50px] text-right text-white font-medium">
                    {filters.rtRating > 0 ? `${filters.rtRating}%+` : 'Visi'}
                  </span>
                </div>
              </div>

              {/* Reset Filters */}
              <div className="flex flex-col">
                <label className="text-xs text-gray-400 opacity-0 pointer-events-none h-5 mb-2">Reset</label>
                <button
                  onClick={() => setFilters({ imdbRating: 0, rtRating: 0, selectedGenre: 'all' })}
                  className="h-11 px-4 bg-dark-700/60 text-gray-300 border border-dark-600/50 rounded-xl text-sm font-medium hover:bg-dark-600/80 hover:text-white hover:border-primary-500/50 transition-all"
                >
                  Atstatyti filtrus
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
        {/* Status bar */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-4 sm:mb-6">
          <div className="flex items-center gap-1.5 sm:gap-2 text-gray-400 text-xs sm:text-sm">
            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <span className="font-medium truncate">{selectedCinemaData?.name}</span>
            <span className="text-dark-600 hidden sm:inline">•</span>
            <span className="capitalize hidden sm:inline">{formatDisplayDate(selectedDate)}</span>
          </div>
          {!loading && data && (
            <span className="text-xs sm:text-sm text-gray-400 font-medium">
              {filteredMovies.length} {filteredMovies.length === 1 ? 'filmas' : 'filmų'}
              {filteredMovies.length !== data.movies?.length && (
                <span className="text-gray-500"> (iš {data.movies?.length})</span>
              )}
            </span>
          )}
        </div>

        {/* Error state */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 sm:p-6 text-center">
            <svg className="w-8 h-8 sm:w-10 sm:h-10 text-red-400 mx-auto mb-2 sm:mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <h3 className="text-base sm:text-lg font-semibold text-red-400 mb-1">Klaida</h3>
            <p className="text-sm sm:text-base text-gray-400 mb-3 sm:mb-4 px-2">{error}</p>
            <button
              onClick={fetchShowtimes}
              className="px-4 py-2 bg-red-600/20 text-red-300 rounded-lg hover:bg-red-600/30 transition-colors text-xs sm:text-sm font-medium"
            >
              Bandyti dar kartą
            </button>
          </div>
        )}

        {/* Loading state */}
        {loading && <LoadingSkeleton />}

        {/* Movies list */}
        {!loading && !error && data && (
          <>
            {filteredMovies.length > 0 ? (
              <div className="space-y-3 sm:space-y-4">
                {filteredMovies.map(movie => (
                  <MovieShowtimeCard key={movie.id} movie={movie} onNavigate={navigate} locationKey={locationKey} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 sm:py-16">
                <svg className="w-12 h-12 sm:w-16 sm:h-16 text-gray-600 mx-auto mb-3 sm:mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z" />
                </svg>
                <h3 className="text-lg sm:text-xl font-semibold text-gray-400 mb-2">
                  {data.movies?.length > 0 ? 'Seansų nerasta' : 'Šiai dienai seansų nėra'}
                </h3>
                <p className="text-sm sm:text-base text-gray-500">
                  {data.movies?.length > 0 
                    ? 'Pabandykite pakeisti filtrus arba pasirinkti kitą datą.' 
                    : 'Šią dieną pasirinktame kino teatre seansų nėra.'}
                </p>
              </div>
            )}
          </>
        )}
      </main>
      </div>
    </Layout>
  );
}

export default CinemaShowtimes;
